import { Badge } from '@skupreme/shared/ui';
import { useSearchParams } from 'react-router';
import {
  useListingTotal,
  useUnmappedCount,
} from '../../hooks/use-unmapped-count';

/** At or above this share the pill is green even with a few left to map. */
export const SUCCESS_PERCENT = 95;

export const MAPPED_EXPLAINER =
  'Each row is one listing, a marketplace SKU or a warehouse SKU, linked to the catalog product it sells. Orders and stock for that listing use the linked product.';

/* Whole percent, rounded down: 100% only once nothing is left to map. */
export const mappedPercent = (mapped: number, unmapped: number) =>
  Math.floor((mapped * 100) / (mapped + unmapped));

/* "Marketplaces 2,900", or nothing while the count is unknown. */
const totalPart = (label: string, total: number | undefined) =>
  total === undefined ? null : `${label} ${total.toLocaleString()}`;

/**
 * The Mapped view's summary: a "NN% mapped" pill and the totals in words.
 * A part whose count is loading or failed shows nothing, never a wrong number.
 */
export function MappedSummary() {
  const [, setParams] = useSearchParams();
  const mapped = useListingTotal('mapped');
  const unmapped = useUnmappedCount();
  const marketplaces = useListingTotal('mapped', 'marketplace');
  const warehouses = useListingTotal('mapped', 'storage_provider');
  const hasBoth =
    mapped !== undefined && unmapped !== undefined && mapped + unmapped > 0;
  const percent = hasBoth ? mappedPercent(mapped, unmapped) : undefined;

  const parts = [
    hasBoth
      ? `${mapped.toLocaleString()} of ${(mapped + unmapped).toLocaleString()} listings mapped to a product`
      : null,
    totalPart('Marketplaces', marketplaces),
    totalPart('Warehouses', warehouses),
  ].filter(part => part !== null);

  return (
    <div className="sk:flex sk:flex-col sk:gap-1">
      <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-3 sk:text-sm sk:text-fg">
        {percent === undefined ? null : (
          <Badge
            tone={
              unmapped === 0 || percent >= SUCCESS_PERCENT
                ? 'success'
                : 'caution'
            }
          >
            {`${percent}% mapped`}
          </Badge>
        )}
        {parts.length ? <span>{parts.join(' · ')}</span> : null}
        {unmapped ? (
          <button
            type="button"
            className="sk:cursor-pointer sk:text-accent sk:underline sk:hover:no-underline"
            onClick={() => setParams({ view: 'to_review' })}
          >
            {`${unmapped.toLocaleString()} to map`}
          </button>
        ) : null}
      </div>
      <p className="sk:text-dense sk:text-fg-secondary">{MAPPED_EXPLAINER}</p>
    </div>
  );
}
